import { useMemo, useState, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchDataFromAPI } from 'app/components/ReusableUIComponents/HighLevelComponents/DataGridMAIQ/api/dataGridApiService';
import type {
  FilterTrayCategory,
  FilterTrayOption,
  FilterTraySelection,
} from 'app/components/ReusableUIComponents/HighLevelComponents/FilterTrayMAIQ';

const TEMPLATES_ENDPOINT = 'api/v2/templates';
const FILTER_TRAY_PAGE_SIZE = 500;

/** Template API fields to show as filter categories (in this order). */
const TEMPLATE_FIELD_KEYS = ['name', 'type', 'generated_type', 'subject'] as const;

function toStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
}

/**
 * From template rows, collect template fields (name, type, generated_type, subject) + metadata keys and their unique values.
 */
function deriveKeysAndValues(
  rows: any[],
  templateFieldKeys: string[],
): { keys: string[]; valueMap: Record<string, string[]> } {
  const valueSetByKey: Record<string, Set<string>> = {};

  for (const row of rows) {
    for (const field of templateFieldKeys) {
      const v = row?.[field];
      if (!valueSetByKey[field]) valueSetByKey[field] = new Set();
      const s = toStr(v).trim();
      if (s !== '') valueSetByKey[field].add(s);
    }
    const meta = row?.metadata;
    if (meta && typeof meta === 'object') {
      for (const [k, v] of Object.entries(meta)) {
        if (!valueSetByKey[k]) valueSetByKey[k] = new Set();
        const s = toStr(v).trim();
        if (s !== '') valueSetByKey[k].add(s);
      }
    }
  }

  const metadataKeys = Object.keys(valueSetByKey)
    .filter(k => !templateFieldKeys.includes(k as any))
    .sort();
  const keys = [...templateFieldKeys, ...metadataKeys];

  const valueMap: Record<string, string[]> = {};
  for (const k of keys) {
    valueMap[k] = Array.from(valueSetByKey[k] ?? []).sort();
  }
  return { keys, valueMap };
}

export interface UseEmailTemplatesFilterTrayArgs {
  dealerId: string;
  statusFilter?: { showActive: boolean; showArchived: boolean };
  includeStatusField?: boolean;
  /** Metadata keys to exclude from the filter categories (e.g. ['segment_field_mapping', 'segment_id']). Case-insensitive. */
  excludedMetadataKeys?: string[];
  initialSelectedByCategory?: FilterTraySelection;
  /**
   * When false, skips the templates prefetch used only to build Quick Filter categories.
   * The grid still loads via DataGridMAIQ. Set true when the filter tray panel is open.
   */
  fetchEnabled?: boolean;
}

export interface UseEmailTemplatesFilterTrayResult {
  categories: FilterTrayCategory[];
  optionsForActiveCategory: FilterTrayOption[];
  selectedByCategory: FilterTraySelection;
  onSelectionChange: (categoryId: string, selectedValues: string[]) => void;
  onClearAllFilters: () => void;
  onClearActiveCategory: () => void;
  onClearAllInPanel: () => void;
  metadataFilters: Record<string, string[]>;
  totalActiveCount: number;
  activeCategoryId: string | null;
  setActiveCategoryId: (id: string | null) => void;
  isLoading: boolean;
  error: Error | null;
}

const DEFAULT_EXCLUDED_METADATA_KEYS = [
  'segment_field_mapping',
  'segment_id',
];

function sortMetadataKeysWithSegmentFirst(keys: string[]): string[] {
  return [...keys].sort((a, b) => {
    const aIsSegment = a.toLowerCase().includes('segment');
    const bIsSegment = b.toLowerCase().includes('segment');

    if (aIsSegment && !bIsSegment) return -1;
    if (!aIsSegment && bIsSegment) return 1;

    return a.localeCompare(b);
  });
}

export function useEmailTemplatesFilterTray({
  dealerId,
  statusFilter,
  includeStatusField = false,
  excludedMetadataKeys = DEFAULT_EXCLUDED_METADATA_KEYS,
  initialSelectedByCategory,
  fetchEnabled = false,
}: UseEmailTemplatesFilterTrayArgs): UseEmailTemplatesFilterTrayResult {
  const [selectedByCategory, setSelectedByCategory] = useState<FilterTraySelection>(
    initialSelectedByCategory ?? {},
  );
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);

  const templateFieldKeys = useMemo(
    () =>
      includeStatusField
        ? [...TEMPLATE_FIELD_KEYS, 'status']
        : [...TEMPLATE_FIELD_KEYS],
    [includeStatusField],
  );

  const showActive = statusFilter?.showActive !== false;
  const showArchived = statusFilter?.showArchived !== false;
  const statusParam =
    showActive && showArchived
      ? undefined
      : [showActive && 'ACTIVE', showArchived && 'ARCHIVED'].filter(Boolean).join(',');

  const metadataFiltersForFetch = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(selectedByCategory)) {
      if (Array.isArray(v) && v.length > 0) out[k] = v;
    }
    return out;
  }, [selectedByCategory]);

  const params = useMemo(() => {
    const filterParams =
      Object.keys(metadataFiltersForFetch).length > 0
        ? Object.fromEntries(
            Object.entries(metadataFiltersForFetch)
              .filter(([, v]) => Array.isArray(v) && v.length > 0)
              .map(([k, v]) => [
                templateFieldKeys.includes(k as any) ? k : `metadata.${k}`,
                (v as string[]).join(','),
              ]),
          )
        : {};
    return {
      dealer: dealerId,
      ...(statusParam && { status: statusParam }),
      limit: FILTER_TRAY_PAGE_SIZE,
      ...filterParams,
    };
  }, [dealerId, statusParam, metadataFiltersForFetch]);

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['email-templates-filter-tray', dealerId, params],
    queryFn: () =>
      fetchDataFromAPI(TEMPLATES_ENDPOINT, params, 1, FILTER_TRAY_PAGE_SIZE),
    enabled: Boolean(dealerId) && fetchEnabled,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  const excludedSet = useMemo(
    () => new Set((excludedMetadataKeys ?? []).map(k => k.toLowerCase())),
    [excludedMetadataKeys],
  );

  const { keys, valueMap } = useMemo(() => {
    const rows = data?.body?.data ?? [];
    const { keys: rawKeys, valueMap: rawValueMap } = deriveKeysAndValues(
      rows,
      templateFieldKeys,
    );
    const templateFields = rawKeys.filter(k => templateFieldKeys.includes(k as any));
    const metadataKeysFiltered = sortMetadataKeysWithSegmentFirst(
      rawKeys
      .filter(k => !templateFieldKeys.includes(k as any))
      .filter(k => !excludedSet.has(k.toLowerCase())),
    );
    const segmentTemplateFields = templateFields.filter(k => k.toLowerCase().includes('segment'));
    const nonSegmentTemplateFields = templateFields.filter(
      k => !k.toLowerCase().includes('segment'),
    );
    const keysFiltered = [
      ...segmentTemplateFields,
      ...metadataKeysFiltered,
      ...nonSegmentTemplateFields,
    ];
    const valueMapFiltered: Record<string, string[]> = {};
    for (const k of keysFiltered) {
      if (rawValueMap[k]) valueMapFiltered[k] = rawValueMap[k];
    }
    return { keys: keysFiltered, valueMap: valueMapFiltered };
  }, [data, excludedSet, templateFieldKeys]);

  const categories: FilterTrayCategory[] = useMemo(
    () =>
      keys.map(id => ({
        id,
        label: id.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        selectedCount: (selectedByCategory[id] ?? []).length,
      })),
    [keys, selectedByCategory],
  );

  const optionsForActiveCategory: FilterTrayOption[] = useMemo(() => {
    if (!activeCategoryId) return [];
    const fromData = valueMap[activeCategoryId] ?? [];
    const applied = selectedByCategory[activeCategoryId] ?? [];
    const combined = Array.from(new Set([...fromData, ...applied])).sort();
    return combined.map(value => ({ value, label: value }));
  }, [activeCategoryId, valueMap, selectedByCategory]);

  const onSelectionChange = useCallback((categoryId: string, selectedValues: string[]) => {
    setSelectedByCategory(prev => ({
      ...prev,
      [categoryId]: selectedValues,
    }));
  }, []);

  const onClearAllFilters = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const onClearActiveCategory = useCallback(() => {
    if (activeCategoryId) {
      setSelectedByCategory(prev => ({ ...prev, [activeCategoryId]: [] }));
    }
  }, [activeCategoryId]);

  const onClearAllInPanel = useCallback(() => {
    setSelectedByCategory({});
  }, []);

  const metadataFilters = useMemo(() => {
    const out: Record<string, string[]> = {};
    for (const [k, v] of Object.entries(selectedByCategory)) {
      if (Array.isArray(v) && v.length > 0) out[k] = v;
    }
    return out;
  }, [selectedByCategory]);

  const totalActiveCount = useMemo(
    () => Object.values(selectedByCategory).reduce((sum, arr) => sum + (arr?.length ?? 0), 0),
    [selectedByCategory],
  );

  return {
    categories,
    optionsForActiveCategory,
    selectedByCategory,
    onSelectionChange,
    onClearAllFilters,
    onClearActiveCategory,
    onClearAllInPanel,
    metadataFilters,
    totalActiveCount,
    activeCategoryId,
    setActiveCategoryId,
    isLoading: !!isLoading || !!isFetching,
    error: error as Error | null,
  };
}
